import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { doc, onSnapshot, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Clock, CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { OvertimeRequestStatus } from '@/types';
import type { QueueTicket } from '@/types';

interface LocationState {
  queueId: string;
  barberId: string;
  barberName: string;
}

export function OvertimeWaitingPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState | null;

  const [ticket, setTicket] = useState<QueueTicket | null>(null);
  const [ticketDeleted, setTicketDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [cancelling, setCancelling] = useState(false);

  // Subscribe to ticket updates
  useEffect(() => {
    if (!state?.queueId) {
      navigate('/take-ticket');
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'queues', state.queueId),
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data() as QueueTicket;
          setTicket({ ...data, queueId: snapshot.id });
        } else {
          // Ticket was deleted (auto-cancelled on expiry or manually cancelled)
          setTicketDeleted(true);
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error subscribing to ticket:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [state?.queueId, navigate]);

  // Desfase entre el reloj del dispositivo y el del servidor.
  // Se calcula una sola vez comparando requestedAt (hora de servidor) con la
  // hora local en el momento de recibir el documento. Sin esta corrección, un
  // móvil con la hora mal ajustada veía la cuenta atrás a 0 desde el principio.
  const clockOffsetRef = useRef<number | null>(null);

  const toMillis = (value: unknown): number | null => {
    if (value instanceof Date) return value.getTime();
    const ts = value as { toMillis?: () => number } | null;
    return typeof ts?.toMillis === 'function' ? ts.toMillis() : null;
  };

  // Cuenta atrás: es SOLO informativa. Nunca decide el estado de la solicitud.
  useEffect(() => {
    const overtime = ticket?.overtimeRequest;
    if (!overtime?.expiresAt) return;

    const expiresAtMs = toMillis(overtime.expiresAt);
    if (expiresAtMs === null) return;

    if (clockOffsetRef.current === null) {
      const requestedAtMs = toMillis(overtime.requestedAt);
      clockOffsetRef.current = requestedAtMs === null ? 0 : Date.now() - requestedAtMs;
    }

    const updateTimer = () => {
      const serverNow = Date.now() - (clockOffsetRef.current ?? 0);
      setTimeRemaining(Math.max(0, Math.floor((expiresAtMs - serverNow) / 1000)));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [ticket?.overtimeRequest?.expiresAt, ticket?.overtimeRequest?.requestedAt]);

  // Format time as HH:MM:SS or MM:SS
  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // Estado de la solicitud: la ÚNICA fuente de verdad es el documento de
  // Firestore. La cuenta atrás local no puede declarar nada expirado, porque
  // el barbero sigue pudiendo aceptar hasta que el servidor lo caduque con su
  // propio reloj (checkExpiredTimers, cada minuto).
  const getStatus = (): 'loading' | 'pending' | 'accepted' | 'rejected' | 'expired' => {
    // El ticket ya no existe (limpieza de fin de día del servidor)
    if (ticketDeleted) return 'expired';

    if (!ticket?.overtimeRequest) return 'loading';

    const status = ticket.overtimeRequest.status;

    if (status === OvertimeRequestStatus.ACCEPTED || ticket.status === 'waiting') {
      return 'accepted';
    }
    if (status === OvertimeRequestStatus.REJECTED) return 'rejected';
    if (status === OvertimeRequestStatus.EXPIRED) return 'expired';

    // El ticket dejó de estar pendiente sin estado final en la solicitud
    if (ticket.status === 'cancelled' || ticket.status === 'expired') {
      return ticket.cancelReason === 'overtime_request_expired' ? 'expired' : 'rejected';
    }

    // Sigue pendiente: aunque el contador local marque 00:00, seguimos
    // esperando. El servidor decidirá si caduca o si el barbero acepta.
    return 'pending';
  };

  // Cancelar: marcar el ticket como cancelado (las reglas de Firestore no
  // permiten borrar un ticket en 'waiting_overtime_approval', por eso el
  // deleteDoc anterior fallaba en silencio y dejaba tickets huérfanos)
  const handleCancel = async () => {
    if (!state?.queueId || cancelling) return;

    setCancelling(true);
    try {
      await updateDoc(doc(db, 'queues', state.queueId), {
        status: 'cancelled',
        'overtimeRequest.status': OvertimeRequestStatus.CANCELLED,
        cancelledAt: serverTimestamp(),
        cancelReason: 'overtime_request_cancelled_by_client',
        updatedAt: serverTimestamp(),
      });
      navigate('/take-ticket');
    } catch (error) {
      console.error('Error cancelling overtime ticket:', error);
      // Navegamos igualmente: el servidor limpiará la solicitud al caducar
      navigate('/take-ticket');
    }
  };

  const status = getStatus();

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-accent mx-auto mb-4" />
          <p className="text-ink-muted">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!state || (!ticket && !ticketDeleted)) {
    return (
      <div className="min-h-screen bg-surface-2 flex items-center justify-center p-4">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-danger mx-auto mb-4" />
          <h2 className="text-xl font-bold text-ink mb-2">
            {t('common.error')}
          </h2>
          <Button onClick={() => navigate('/take-ticket')}>
            {t('booking.overtimeWaiting.chooseAnother')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Pending State */}
        {status === 'pending' && (
          <div className="bg-surface rounded-3xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="p-8 text-center">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <Clock className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {t('booking.overtimeWaiting.title')}
              </h1>
              <p className="text-white/90">
                {timeRemaining > 0
                  ? t('booking.overtimeWaiting.subtitle')
                  : t('booking.overtimeWaiting.noResponseYetSubtitle')}
              </p>
            </div>

            {/* Content */}
            <div className="p-8">
              {/* Barber info */}
              <div className="text-center mb-8">
                <p className="text-ink-muted mb-2">
                  {t('booking.overtimeWaiting.waitingFor')}
                </p>
                <p className="text-2xl font-bold text-ink">
                  {state.barberName}
                </p>
              </div>

              {/* Timer (o aviso de que seguimos esperando si ya llegó a 0) */}
              <div className="bg-accent/10 rounded-2xl p-6 text-center mb-6">
                {timeRemaining > 0 ? (
                  <>
                    <p className="text-sm text-accent mb-2">
                      {t('booking.overtimeWaiting.timeRemaining')}
                    </p>
                    <p className="text-5xl font-mono font-bold text-accent">
                      {formatTime(timeRemaining)}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-accent">
                    {t('booking.overtimeWaiting.noResponseYet')}
                  </p>
                )}
              </div>

              {/* Loading indicator */}
              <div className="flex items-center justify-center gap-2 text-ink-subtle mb-6">
                <div className="w-2 h-2 bg-accent/100 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-accent/100 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-accent/100 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>

              {/* Cancel button */}
              <Button
                onClick={handleCancel}
                disabled={cancelling}
                variant="ghost"
                className="w-full text-ink-subtle hover:text-ink-muted "
              >
                {cancelling ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('common.cancelling') || 'Cancelando...'}
                  </span>
                ) : (
                  t('booking.overtimeWaiting.cancelAndChooseAnother')
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Accepted State */}
        {status === 'accepted' && (
          <div className="bg-surface rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {t('booking.overtimeWaiting.accepted')}
              </h1>
              <p className="text-white/90">
                {t('booking.overtimeWaiting.acceptedMessage', { barberName: state.barberName })}
              </p>
            </div>

            <div className="p-8">
              <Button
                onClick={() => navigate('/client-queue')}
                className="w-full h-14 text-lg font-bold text-white"
              >
                {t('booking.overtimeWaiting.goToQueue')}
              </Button>
            </div>
          </div>
        )}

        {/* Rejected State */}
        {status === 'rejected' && (
          <div className="bg-surface rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {t('booking.overtimeWaiting.rejected')}
              </h1>
              <p className="text-white/90">
                {t('booking.overtimeWaiting.rejectedMessage', { barberName: state.barberName })}
              </p>
            </div>

            <div className="p-8">
              {ticket?.overtimeRequest?.rejectionNotes && (
                <div className="bg-surface-2 rounded-xl p-4 mb-6">
                  <p className="text-sm text-ink-muted">
                    {ticket.overtimeRequest.rejectionNotes}
                  </p>
                </div>
              )}

              <Button
                onClick={() => navigate('/take-ticket')}
                className="w-full h-14 text-lg font-bold"
              >
                {t('booking.overtimeWaiting.chooseAnother')}
              </Button>
            </div>
          </div>
        )}

        {/* Expired State */}
        {status === 'expired' && (
          <div className="bg-surface rounded-3xl shadow-2xl overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-12 h-12 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-2">
                {t('booking.overtimeWaiting.expired')}
              </h1>
              <p className="text-white/90">
                {t('booking.overtimeWaiting.expiredMessage')}
              </p>
            </div>

            <div className="p-8">
              <Button
                onClick={() => navigate('/take-ticket')}
                className="w-full h-14 text-lg font-bold"
              >
                {t('booking.overtimeWaiting.chooseAnother')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
